<template>
  <teleport to="#modal_backdrop">
    <div @click="$emit('exit-modal')" class="backdrop-popup"></div>
    <div class="modal-popup">
      <button @click="$emit('exit-modal')" class="close-button">×</button>
      <h1 class="felix007 center">היסטוריית הזמנות למוצר</h1>
      <div class="orders-container">
        <!-- Loading Skeletons -->
        <template v-if="isLoading">
          <div v-for="n in 3" :key="n" class="order-card skeleton">
            <div class="order-header">
              <div class="skeleton-text"></div>
              <div class="skeleton-badge"></div>
            </div>
            <div class="order-details">
              <div class="skeleton-text"></div>
              <div class="skeleton-text"></div>
              <div class="skeleton-line"></div>
            </div>
          </div>
        </template>

        <!-- Empty State -->
        <div v-else-if="!productOrdersHistory.length" class="empty-state">
          <span>{{ emptyOrdersMessage }}</span>
        </div>

        <!-- Orders List -->
        <template v-else>
          <div
            v-for="order in productOrdersHistory"
            :key="order.ord"
            class="order-card"
            @click="gotoOrderPage(order.ord)"
          >
            <div class="order-header">
              <span class="order-number">הזמנה: {{ order.ord }}</span>
              <span class="order-status">
                <span :style="getOrderStatusColor(order)"
                  ><img
                    v-if="order.Status.icon"
                    :src="`/images/${order.Status.icon}`"
                    alt="status-icon"
                    class="status-icon"
                  />
                  {{ order.Status.statusname }}</span
                >
              </span>
            </div>
            <div class="order-details">
              <div class="detail-row">
                <span class="label">תאריך הזמנה:</span>
                <span>{{ formatDate(order.duedate) }}</span>
              </div>
              <div v-if="false" class="detail-row">
                <span class="label">לקוח:</span>
                <span>{{ order.cust }}</span>
              </div>
              <div class="order-lines">
                <div
                  v-for="(line, index) in order.OrderLines"
                  :key="index"
                  class="line-item"
                >
                  <span>כמות: {{ line.quant }}</span>
                  <span>מחיר: ₪{{ (line.partPrice).toFixed(2) }}</span>
                  <span>סה"כ: ₪{{ (line.quant * line.partPrice).toFixed(2) }}</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </teleport>
</template>

<script>
import OrderService from "../../services/OrderService";
import { routerNames } from "@/router";

export default {
  emits: ["exit-modal"],
  props: {
    part: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      productOrdersHistory: [],
      emptyOrdersMessage: "לא נמצאו הזמנות למוצר זה",
      isLoading: false,
    };
  },
  async mounted() {
    this.isLoading = true;
    await this.getPartOrdersHistory();
    this.isLoading = false;
  },
  methods: {
    gotoOrderPage(ord) {
      this.$router.push({
        name: routerNames.order,
        params: {
          ord: ord,
        },
      });
    },
    getOrderStatusColor(order) {
      switch (order.status) {
        case 1:
        case 7:
        case 4: // חלקית/אושרה להפצה או סופקה
          return "color: #71C0A3";
        case 3:
        case 6: // ממתינה לביטול/מבוטלת
          return "color: #FFFFFF";
        case 5:
        case 2: // נקלטה/ממתינה לקליטה
          return "color: #FFE6AD";
        default:
          return "";
      }
    },
    async getPartOrdersHistory() {
      try {
        const response = await OrderService.getCustOrdersHistoryByPart(
          this.part
        );
        if (response) {
          this.productOrdersHistory = response.data;
        } else {
          this.productOrdersHistory = [];
        }
      } catch (error) {
        console.log(error);
        if (error?.response?.data?.error) {
          this.emptyOrdersMessage = error.response.data.error;
        }
        this.productOrdersHistory = [];
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("he-IL", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },
  },
};
</script>

<style scoped lang="scss">
.backdrop-popup {
  background-color: #000000;
  opacity: 0.6;
  position: fixed;
  width: 100vw;
  height: 100vh;
  top: 0;
  z-index: 11;
}

.modal-popup {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  z-index: 12;
  font-size: $mediumTextSize;
  border-radius: 40px;
  background: #393945;
  padding: 20px;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 500px; // Increased width for better display
  max-height: 90vh;
  min-height: 400px; // Add minimum height
  height: auto;
  overflow: hidden; // Changed from overflow-y: auto

  /* Custom scrollbar styles */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  /* Firefox scrollbar styles */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;

  h1 {
    margin: 0;
    padding: 0;
    color: white;
    font-size: $largestTextSize;
    letter-spacing: 0.04px;
  }
}

.orders-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-height: calc(80vh - 100px); // Adjust based on header height
  min-height: 300px; // Add minimum height for content area
  height: calc(100% - 60px); // Subtract header height and padding
  overflow-y: auto;
  padding-right: 10px;

  /* Custom scrollbar styles */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  /* Firefox scrollbar styles */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.order-card {
  background: #2c2c35;
  cursor: pointer;
  border-radius: 10px;
  padding: 15px;
  width: 100%;
  transition: box-shadow 0.3s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
    transform: translateY(-2px);
  }
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  .order-number {
    font-weight: bold;
    color: white;
  }

  .order-status {
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #4a4a57;
    color: white;
    font-size: 1em;
  }
}

.order-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  color: #e0e0e0;

  .label {
    color: #9e9e9e;
  }
}

.order-lines {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #4a4a57;
}

.line-item {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
  color: #e0e0e0;
}

.skeleton {
  .skeleton-text {
    height: 20px;
    background: linear-gradient(90deg, #2c2c35 25%, #393945 50%, #2c2c35 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 4px;
    margin: 5px 0;
  }

  .skeleton-badge {
    width: 80px;
    height: 24px;
    background: linear-gradient(90deg, #2c2c35 25%, #393945 50%, #2c2c35 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 4px;
  }

  .skeleton-line {
    height: 1px;
    background: #4a4a57;
    margin: 10px 0;
  }
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  color: #9e9e9e;
  text-align: center;
  background: #2c2c35;
  border-radius: 10px;
  width: 100%;
  min-height: 300px; // Match orders-container min-height
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.status-icon {
  max-width: 22px;
  max-height: 22px;
}

.close-button {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: #fff;
  font-size: 34px;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &:focus {
    outline: none;
    background-color: rgba(255, 255, 255, 0.15);
  }
}
</style>
